import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useClientFetch } from "~/composables/useClientFetch.js";
import {getFromServer} from "~/composables/getFromServer.js";
import {useMenuStore} from '~/stores/MenuStore.js';
import {getFromAny} from "~/composables/getFromAny.js";
import {usePageMetadata} from "~/composables/useMeta.js";
import {useSettingsStore} from "~/stores/SettingsStore.js";
import { useLocalePath } from '#imports';

export const useLocaleStore = defineStore('locale', () => {
    const languages = ref([]);
    const languagesList = ref([]);
    const locale = ref('en-GB');
    const prefix = ref('en');
    const currencies = ref([]);
    const currency = ref([]);
    const ship_country = ref({});
    const internalCountries = ref([]);

    const countries = computed(async () => {
        if (internalCountries.value.length === 0) {
            internalCountries.value = await getFromAny('countries');
        }

        return internalCountries.value;
    });

    const isLanguageSwitchNavigation = ref(false);
    const allowShipToCountryChange = ref(true);
    const allowCurrencyChange = ref(true);

    function markAsLanguageSwitch() {
        isLanguageSwitchNavigation.value = true;
    }

    function clearLanguageSwitch() {
        isLanguageSwitchNavigation.value = false;
    }

    function initData(data) {
        languages.value = data.languages;
        languagesList.value = languages.value.reduce((map, language) => {
            map[language.prefix] = language.name;
            return map;
        }, {});
        locale.value = data.locale;
        prefix.value = data.prefix;
        currencies.value = data.currencies;
        currency.value = data.currency;
        setShipToCountry(data.ship_country);
    }

    async function fetchLanguages() {
        const data = await getFromServer('locale');
        initData(data);
    }

    function getLanguage() {
        return prefix.value || 'en';
    }

    function getLanguageInstance() {
        return languages.value.find((language) => language.prefix === prefix.value);
    }

    async function changeLanguage(language) {
        if (language === prefix.value) {
            return;
        }
        prefix.value = language;

        const data = await getFromAny('/locale/language', {
            method: 'POST',
            body: {language, path: useRoute().path}
        });
        this.initData(data.locale);
        useMenuStore().initData(data.menu);
        useSettingsStore().initData(data);
        const localePath = useLocalePath();
        if ((localePath({name: 'index'}) + '/') === useRoute().path) {
            usePageMetadata().setSchema(data.homepage_data);
            usePageMetadata().setMetaData(data.homepage_data);
        }

        useHead({
            htmlAttrs: {
                lang: language
            },
        });
    }

    async function changeCountry(code) {
        if (code === ship_country.value.code) return;

        const response = await useClientFetch('/ship-to-country', {
            method: 'POST',
            body: {code}
        });

        setShipToCountry(response.data.country);
    }

    function setShipToCountry(shipToCountry) {
        ship_country.value = shipToCountry;
    }

    function changeCurrency(inputCurrency) {
        currency.value = inputCurrency;
    }

    return {
        languages,
        languagesList,
        locale,
        prefix,
        currencies,
        countries,
        currency,
        ship_country,
        setShipToCountry,
        initData,
        changeCurrency,
        changeLanguage,
        changeCountry,
        getLanguage,
        isLanguageSwitchNavigation,
        markAsLanguageSwitch,
        clearLanguageSwitch,
        allowShipToCountryChange,
        allowCurrencyChange,
        getLanguageInstance
    };
});
